import * as angular from 'angular';
import * as _ from 'lodash';
import * as $ from 'jquery';

import {
    ActualDataType,
    ComponentDefinition,
    CreateDigitalCorrespondanceTable,
    Dictionary,
    DigitalCorrespondanceLayer,
    DigitalCorrespondanceTable,
    DigitalCorrespondanceTableRow,
    DigitalSet,
    DigitalSetStateViewModels,
    DigitalState,
    DropObject,
    Identifier,
    ObjectUser,
    TagDefinition
} from '../../../../models';
import { toggleEditMode } from '../../../../common';
import { correspondanceTable, guidEmpty } from '../../../../common/constants';
import { IDigitalSetService, ILoggerService, IStoreService } from '../../../../services';
import { Store, StoreElement } from '../../../../stateManagement';
import { getRows } from '../correspondanceTree/buildCorrespondanceTree';
import { SubPageScope } from 'states/moreTools/SubPageScope';

class DigitalSetsStateController implements angular.IComponentController, angular.IOnDestroy, DigitalSetStateViewModels {
    correspondanceTableDictionary: Dictionary<DigitalCorrespondanceTable>;
    correspondanceTables: DigitalCorrespondanceTable[];
    countDictionary: Dictionary<number>;
    digitalSetDictionary: Dictionary<DigitalSet>;
    digitalSets: DigitalSet[];
    guidEmpty: string;
    identifiers: Dictionary<Identifier>;
    isEditMode: boolean;
    isLoading: boolean;
    loadingMessage: string;
    selectedSet: DigitalSet;
    /**
     * The currently selected table. This is a copy from the object of the store.
     */
    selectedTable: DigitalCorrespondanceTable;
    subPageScope: SubPageScope;
    treeRows: DigitalCorrespondanceTableRow[];
    usageDictionary: Dictionary<ObjectUser[]>;

    readonly contextualController: angular.IFormController | null;

    private _unsubscribe: Function;

    static $inject = [
        '$q',
        '$scope',
        '$translate',
        '$timeout',
        'storeService',
        'digitalSetService',
        'loggerService'
    ];
    constructor(
        private _$q: angular.IQService,
        private _$scope: angular.IScope,
        private _$translate: angular.translate.ITranslateService,
        private _$timeout: angular.ITimeoutService,
        private _storeService: IStoreService,
        private _digitalSetService: IDigitalSetService,
        private _loggerService: ILoggerService
    ) {
    }

    $onInit() {
        this.guidEmpty = guidEmpty;
        this.selectedSet = null;
        this.correspondanceTableDictionary = {};
        this.correspondanceTables = [];
        if (this.isEditMode) {
            this.loadUsages();
        }
        this.subPageScope = {
            onCancelClick: this.onCancelClick.bind(this)
        };
        this.isLoading = true;
        this._unsubscribe = this._storeService.register(
            store => this.updateStore(store),
            [
                StoreElement.DigitalCorrespondanceTables,
                StoreElement.DigitalSets,
                StoreElement.Identifiers,
                StoreElement.Layout,
                StoreElement.Tags,
            ]
        );

        this.refresh();
        this._$scope.$watch(() => this.selectedSet, () => this.clearCreating());
        this._$scope.$on(toggleEditMode, (event, data) => {
            if (this.isEditMode && !this.usageDictionary) {
                this.loadUsages();
            }
            if (this.selectedTable) {
                this.getTreeRows();
            }
        });
    }

    $onDestroy(): void {
        if (this._unsubscribe) {
            this._unsubscribe();
        }
    }

    //#region Digital sets
    select(item: DigitalSet | null, $event: angular.IAngularEvent) {
        if (this.contextualController?.$dirty) {
            this._loggerService.logWarning(this._$translate.instant('Save or cancel changes before selecting another item'));
        } else {
            this.selectedSet = item;
            this.selectedTable = null;
            if ($event.preventDefault) {
                $event.preventDefault();
            }
            $event.stopPropagation();
        }
    }

    onDrop(item: DropObject) {
        if (item?.itemId === 'digitalSet') {
            const newItem: any = {
                name: null,
                shortComment: null,
                longComment: null,
                stateCollection: [],
                guid: guidEmpty
            };
            this.digitalSets = this.digitalSets.concat([newItem]);
            this.selectedSet = newItem;
        }
    }

    onDelete(set: DigitalSet) {
        this._storeService.dispatchers().digitalSets
            .deleteOne(set.guid)
            .then(() => {
                this._loggerService.logSuccess(`Set ${set.name} successfully deleted`);
                this.selectedSet = null;
            })
            .catch(() => {
                this._loggerService.logError('Failed to delete set ' + set.name);
            });
    }

    onDeleteClick(set: DigitalSet, $event: angular.IAngularEvent) {
        this.select(set, $event);
        $('#deleteDigitalSetModal').modal('show');
    }

    addState(set: DigitalSet, state: DigitalState) {
        const { stateCollection } = set;
        const nextIndex = stateCollection.length ? Math.max(...stateCollection.map(d => d.code)) + 1 : 0;
        stateCollection.push({
            code: nextIndex,
            name: ''
        });
    }

    onRemoveState(state: DigitalState) {
        this.removeState(this.selectedSet, state);
    }

    removeState(set: DigitalSet, state: DigitalState) {
        _.remove(set.stateCollection, state);
        this.contextualController?.$setDirty();
    }

    download(set: DigitalSet) {
        this._digitalSetService.download(set);
    }

    private loadUsages(): angular.IPromise<Dictionary<ObjectUser[]>> {
        this.loadingMessage = 'Loading usages';
        this.isLoading = true;
        return this._digitalSetService.getUsers(true)
            .then(data => this.usageDictionary = data)
            .finally(() => this.isLoading = false);
    }

    // update selected set if sets have changed
    private reselect() {
        if (!this.contextualController || !this.contextualController.$dirty) {
            if (this.digitalSets?.length > 0 && this.selectedSet) {
                this.selectedSet = this.digitalSets.find(set => set.guid === this.selectedSet.guid);
            }
            if (this.correspondanceTables?.length > 0 && this.selectedTable) {
                const selectedTable = this.correspondanceTables.find(d => d.guid === this.selectedTable.guid);
                this.onTableClick(selectedTable);
            }
        }
    }

    private clearCreating() {
        if (!this.selectedSet && _.last(this.digitalSets)?.guid === guidEmpty) {
            this.digitalSets.pop();
        }
    }
    //#endregion

    refresh(force?: boolean) {
        this.isLoading = true;
        const promises = [
            this._storeService.dispatchers().digitalSets.getAll(!!force),
            this._storeService.dispatchers().digitalCorrespondanceTables.getAll(!!force)
        ];
        return this._$q.all(promises)
            .then(data => {
                const dic: Dictionary<DigitalCorrespondanceTable> = {};
                data[1].forEach(d => {
                    dic[d.guid] = d;
                });
                this.correspondanceTableDictionary = dic;
                this.correspondanceTables = _.values(this.correspondanceTableDictionary);
                this.reselect();
            })
            .finally(() => this.isLoading = false);
    }

    private updateStore(store: Store): void {
        const { digitalSets, digitalCorrespondanceTables, tags } = store;
        const countDic: Dictionary<number> = {};
        const sets = _.values<DigitalSet>(digitalSets.dictionary);
        const tagList = _.values<TagDefinition>(tags.dictionary);

        sets.forEach(s => {
            const count = tagList.filter(t => t.actualDataType === ActualDataType.Digital && t.digitalSetGuid === s.guid).length;
            countDic[s.guid] = count;
        });

        this.countDictionary = countDic;
        this.digitalSetDictionary = digitalSets.dictionary;
        this.digitalSets = sets;
        this.correspondanceTableDictionary = digitalCorrespondanceTables.dictionary;
        this.correspondanceTables = _.values(this.correspondanceTableDictionary);
        this.identifiers = store.identifiers.dictionary;
        this.isEditMode = store.layout.value.isEditMode;
        this.isLoading = false;
        this._$timeout(() => {
            this.reselect();
        });
    }

    onCancelClick() {
        this.selectedSet = null;
        this.selectedTable = null;
        this.refresh();
    }

    //#region Correspondance tables
    onDropCorrespondanceTable(item: DropObject) {
        if (item.itemId === correspondanceTable) {
            const newTable: CreateDigitalCorrespondanceTable = {
                guid: guidEmpty,
                layers: [],
                name: '',
                associationTable: []
            };
            const obj = {};
            obj[newTable.guid] = newTable;
            this.correspondanceTableDictionary = Object.assign(
                obj,
                this.correspondanceTableDictionary
            );
            this.correspondanceTables = _.values(this.correspondanceTableDictionary);
            this.onTableClick(newTable as any);
            this._$timeout(() => {
                if (this.contextualController) {
                    this.contextualController.$setDirty();
                    this.validateLayers();
                }
            });
        }
    }

    onLayerDeleteClick(layer: DigitalCorrespondanceLayer, $index: number) {
        if (this.selectedTable) {
            this.selectedTable.layers.splice($index, 1);
            this.selectedTable.associationTable = this.selectedTable.associationTable
                .filter(d => d.digitalSetGuid !== layer.digitalSetGuid);
            this.rebuildLocalObject();
            if (this.contextualController) {
                this.contextualController.$setDirty();
                this.validateLayers();
            }
        }
    }

    onTableSetDrop(table: DigitalCorrespondanceTable, dsGuid: string, targetLayerSetGuid?: string) {
        if (this.isEditMode) {
            if (!this.selectedTable || this.contextualController.$pristine) {
                this.onTableClick(table);
            } else {
                this._loggerService.logWarning(this._$translate.instant('Save or cancel changes before selecting another item'));
            }
            if (this.selectedTable.guid === table.guid) {
                table = this.selectedTable;
                if (table) {
                    const layerGuids = table.layers
                        .sort((a, b) => a.level < b.level ? -1 : 1)
                        .map(d => d.digitalSetGuid);
                    const index = layerGuids.indexOf(dsGuid);
                    if (index < 0) {
                        // Use timeout in case the table had to be selected. In that case, need to wait for form controller to be available.
                        this._$timeout(() => {
                            const newLayer = {
                                level: table.layers.length ? _.max(table.layers.map(l => l.level)) + 1 : 0,
                                digitalSetGuid: dsGuid
                            };
                            if (!targetLayerSetGuid) {
                                table.layers = table.layers.concat([newLayer]);
                            } else {
                                const targetIndex = layerGuids.indexOf(targetLayerSetGuid);
                                newLayer.level = targetIndex;
                                const newArray = table.layers
                                    .map(d => d);
                                newArray.splice(targetIndex, 1, newLayer);
                                table.layers = newArray;
                            }
                            if (this.contextualController) {
                                this.contextualController.$setDirty();
                                this.validateLayers();
                            }
                            this.rebuildLocalObject();
                        });
                    }
                }
            }
        }
    }

    private validateLayers() {
        const controls = this.contextualController.$getControls()
            .filter(d => d.$name === 'nameProperty');
        const isValid = this.selectedTable.layers.length >= 2;
        if (controls.length) {
            controls[0].$setValidity('levels', isValid, this.contextualController);
        } else {
            this.contextualController.$setValidity('levels', isValid, this.contextualController);
        }
    }

    /**
     * Rebuild objects to trigger undelying components refresh
     */
    private rebuildLocalObject() {
        const obj = {};
        obj[this.selectedTable.guid] = this.selectedTable;
        this.correspondanceTableDictionary = Object.assign({}, this.correspondanceTableDictionary, obj);
        this.correspondanceTables = _.values(this.correspondanceTableDictionary);
        this.getTreeRows();
    }

    onTableClick(table: DigitalCorrespondanceTable, $event?: angular.IAngularEvent) {
        if (this.contextualController?.$dirty) {
            this._loggerService.logWarning(this._$translate.instant('Save or cancel changes before selecting another item'));
        } else {
            if (this.selectedSet) {
                this.selectedSet = null;
            }
            if (table) {
                // Let's work on a copy of the object to avoid mutating the store
                this.selectedTable = Object.assign({}, table, {
                    layers: table.layers.map(d => Object.assign({}, d)),
                    associationTable: table.associationTable.map(d => Object.assign({}, d))
                });
                this.getTreeRows();
            } else {
                this.selectedTable = table;
            }
        }
        this.stopPropagation($event);
    }

    onTableDelete(table: DigitalCorrespondanceTable) {
        this.onTableClick(table);
        $('#deleteCorrespondanceTableModal').modal('show');
    }

    onRowCheckChange(item: DigitalCorrespondanceTableRow, isChecked: boolean) {
        if (this.selectedTable) {
            const associationTable = this.selectedTable.associationTable || [];
            if (isChecked) {
                associationTable.push(item);
            } else {
                const index = associationTable.indexOf(associationTable.filter(a => a.guid === item.guid)[0]);
                associationTable.splice(index, 1);
            }
            this.selectedTable.associationTable = associationTable;
            this.getTreeRows();
            if (this.contextualController) {
                this.contextualController.$setDirty();
            }
        }
    }

    deleteCorrespondanceTable(item: DigitalCorrespondanceTable) {
        if (item.guid !== guidEmpty) {
            this.isLoading = true;
            return this._storeService.dispatchers().digitalCorrespondanceTables
                .deleteOne(item.guid)
                .then(() => {
                    this.selectedTable = null;
                })
                .finally(() => {
                    this.isLoading = false;
                });
        } else {
            const newDic = Object.assign({}, this.correspondanceTableDictionary);
            delete (newDic[guidEmpty]);
            this.correspondanceTableDictionary = newDic;
            this.correspondanceTables = _.values(this.correspondanceTableDictionary);
            this.selectedTable = null;
            return this._$q.when();
        }
    }

    private getTreeRows() {
        const rows = this.isEditMode
            ? getRows(this.selectedTable, this.digitalSetDictionary)
            : (this.selectedTable.associationTable || []);
        this.treeRows = rows;
    }

    //#endregion

    private stopPropagation($event: angular.IAngularEvent) {
        if ($event) {
            if ($event.preventDefault) {
                $event.preventDefault();
            }
            $event.stopPropagation();
        }
    }
}

export const wtDigitalSetsState: ComponentDefinition = {
    componentId: 'wtDigitalSetsState',
    controller: DigitalSetsStateController,
    template: require('./digitalSetsState.component.html'),
    controllerAs: 'vm',
    bindings: {
        contextualController: '<?',
        correspondanceTables: '=',
        digitalSets: '=?',
        formController: '=?',
        selectedSet: '=?',
        selectedTable: '=?',
        subPageScope: '=?'
    }
};